import React, { useState, useEffect } from "react";
import axios from "axios";
import { BASE_URL } from "../../../env";
import Sidebar from "../../../components/layout/Sidebar";
import { Form, Row, Col } from "react-bootstrap";
import {
  getFirstAndLastEmiDates,
  calculateEmiAmount,
  generateLoanSummaryForMember,
  formatAmount,
  formatISODateToLocalDMY,
  isValidPaidReceipt,
  resolveEffectiveStartDate,
} from "../../../utils/utils";
import CustomPagination from "../../../components/ui/CustomPagination";
import DataTable from "../../../components/ui/DataTable";
import { useAdminAuth } from "../../auth/AdminAuthProvider";

import DashboardHeader from "../../../components/layout/DashboardHeader";
import { MdExpandMore } from "react-icons/md";
import StandardizedDateInput from "../../../components/ui/StandardizedDateInput";
import Select from "react-select";

function GplMasterDataReport() {
  const { adminUser } = useAdminAuth();
  const [filteredData, setFilteredData] = useState([]);
  const [openSidebarToggle, setOpenSidebarToggle] = useState(window.innerWidth > 1000);
  const [currentPage, setCurrentPage] = useState(1);
  const [fromDate, setFromDate] = useState("2024-07-01");
  const [toDate, setToDate] = useState("");
  const [selectedBranchFilter, setSelectedBranchFilter] = useState(null);
  const [selectedEmployeeFilter, setSelectedEmployeeFilter] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Rows are now fetched one page at a time from the server rather than the
  // whole report being downloaded and sliced in the browser.
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [serverBranchOptions, setServerBranchOptions] = useState([]);
  const [serverEmployeeOptions, setServerEmployeeOptions] = useState([]);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  const itemsPerPage = 20;
  const indexOfFirstItem = (currentPage - 1) * itemsPerPage;
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const paginationData = filteredData;

  // Typing used to filter rows already in the browser. Now each change is a
  // request, so wait until typing pauses before asking the server.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Any change of filter puts the user back on the first page, matching the
  // previous behaviour where filtering reset the page.
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, fromDate, toDate, selectedBranchFilter, selectedEmployeeFilter]);

  // Adds the per-row figures the report shows. This was always calculated per
  // row, so it works exactly the same on one page as on the whole report.
  const enhanceRows = (rows, effectiveToDate) =>
    (Array.isArray(rows) ? rows : []).map((item) => {
      const emiMonthsPaid = item.receiptsDetails
        ? new Set(
            item.receiptsDetails
              .filter((receipt) => isValidPaidReceipt(receipt))
              .map((receipt) => receipt.emiDate)
          ).size
        : 0;

      const loanSummary = generateLoanSummaryForMember(
        item,
        "2000-01-01",
        effectiveToDate || new Date().toISOString().split("T")[0]
      );

      return { ...item, emiMonthsPaid, ...loanSummary };
    });

  // The filters the server understands. Branch and employee are sent by name,
  // exactly as the dropdowns present them.
  const buildQueryParams = () => ({
    id: adminUser?.user?.id,
    ...(debouncedSearchTerm.trim() !== "" && { search: debouncedSearchTerm.trim() }),
    ...(fromDate && toDate && { fromDate, toDate }),
    ...(selectedBranchFilter && { branchName: selectedBranchFilter.value }),
    ...(selectedEmployeeFilter && { username: selectedEmployeeFilter.value }),
  });

  const OpenSidebar = () => {
    setOpenSidebarToggle(!openSidebarToggle);
  };

  useEffect(() => {
    let cancelled = false;

    const getReportData = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/api/getGplmasterreport`, {
          params: {
            ...buildQueryParams(),
            page: currentPage,
            pageSize: itemsPerPage,
          },
        });
        if (cancelled) return;

        const payload = response.data || {};
        const rows = Array.isArray(payload.rows) ? payload.rows : [];
        setFilteredData(enhanceRows(rows, toDate));
        setTotalItems(payload.totalCount || 0);
        setTotalPages(payload.totalPages || 0);
        // Built by the server over the whole accessible set, so choosing a
        // filter never removes the other choices from the dropdowns.
        setServerBranchOptions(payload.filterOptions?.branches || []);
        setServerEmployeeOptions(payload.filterOptions?.employees || []);
      } catch (error) {
        if (cancelled) return;
        console.error("Error fetching Data:", error);
        setFilteredData([]);
        setTotalItems(0);
        setTotalPages(0);
      }
    };

    if (adminUser?.user?.id) {
      getReportData();
    }

    // Guards against an earlier, slower request overwriting a newer one.
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    adminUser?.user?.id,
    currentPage,
    debouncedSearchTerm,
    fromDate,
    toDate,
    selectedBranchFilter,
    selectedEmployeeFilter,
  ]);

  const branchOptions = serverBranchOptions.map((name) => ({
    value: name,
    label: name,
  }));

  const employeeOptions = serverEmployeeOptions.map((name) => ({
    value: name,
    label: name,
  }));

  // Filtering, paging and the dropdown options are all handled by the server
  // now (see the fetch above). The rows held here are already the page the user
  // is looking at, enhanced by enhanceRows.

  // The screen only holds one page, but an export must contain the whole
  // report. Requesting without page/pageSize returns every row matching the
  // current filters, which is then enhanced the same way the page is.
  const fetchAllRowsForExport = async () => {
    const response = await axios.get(`${BASE_URL}/api/getGplmasterreport`, {
      params: buildQueryParams(),
    });
    const rows = Array.isArray(response.data) ? response.data : [];
    return enhanceRows(rows, toDate);
  };

  const columns = [
    { label: "S No", render: (data, index, isExcel) => isExcel ? index + 1 : indexOfFirstItem + index + 1 },
    { label: "Region", render: (data) => data.regionName },
    { label: "Division", render: (data) => data.divisionName },
    { label: "Branch Name", render: (data) => data.branchName },
    { label: "Branch Code", render: (data) => data.branchCode },
    { label: "Customer Name", render: (data) => data.memberName },
    { label: "Customer ID", render: (data) => data.customerId },
    {
      label: "Address",
      render: (data) => {
        const parts = [
          data.permanentDoorNo,
          data.permanentStreetNo,
          data.permanentPanchayat,
          data.permanentTaluk,
          data.permanentDistrict,
          data.permanentPincode
        ].filter(part => part && String(part).trim() !== '');
        return parts.length > 0 ? parts.join(", ") : "N/A";
      },
    },
    { label: "Contact No", render: (data) => data.phoneNumber },
    { label: "Loan Type", render: (data) => data.loanType },
    { label: "Loan Status", render: (data) => data.loanStatus },

    {
      label: "Sub Category",
      render: (data) => data.businessDetails?.natureOfBusiness || "-",
    },
    { label: "Loan Cycle", render: (data) => data.loanCycle },
    { label: "Funder Name", render: (data) => data.agencyName },
    { label: "Loan Account No", render: (data) => data.loanId },
    {
      label: "Rate of Interest",
      render: (data) => data.proposedLoanDetails?.rateOfInterest || "-",
    },
    {
      label: "Disbursement Date",
      render: (data) =>
        data.branchManagerStatus === "disbursed" &&
          data.branchManagerStatusUpdatedAt
          ? new Date(data.branchManagerStatusUpdatedAt).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          })
          : "",
    },
    {
      label: "Maturity Date",
      render: (data) => {
        const { lastEmiDate } = getFirstAndLastEmiDates(
          data.branchManagerStatusUpdatedAt,
          data.proposedLoanDetails.tenureInMonths,
          data.emiDateByBranchManager,
          resolveEffectiveStartDate(data)
        );
        return lastEmiDate ? lastEmiDate.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" }) : "";
      },
    },
    { label: "Booking Day Gold Rate", render: (data) => data.fk_member_details_hasOne_booking_process_bm_memberId?.goldRate },
    { label: "Gram", render: (data) => data.ornamentWeightByBm },
    { label: "Gold Amount", render: (data) => formatAmount(data.ornamentWeightValueByBm) || 0 },
    { label: "Wastage Amount", render: (data) => formatAmount(data.wastageValueByBm) || 0 },
    { label: "Gst Amount", render: (data) => formatAmount(data.gstValueByBm) || 0 },

    {
      label: "Gold Loan",
      render: (data) => formatAmount(data.goldloantotal) || 0,
    },
    {
      label: "Cash Loan",
      render: (data) => formatAmount(data.cashtloanotal) || 0,
    },
    {
      label: "Disbursement Amount",
      render: (data) =>
        data.branchManagerStatus === "disbursed"
          ? formatAmount(data.eligibleLoanAmountByBm)
          : "-",
    },
    {
      label: "Tenure In Months",
      render: (data) => data.proposedLoanDetails?.tenureInMonths || "-",
    },
    {
      label: "EMI months Paid",
      render: (data) => data.emiMonthsPaid,
    },
    { label: "Employee Name", render: (data) => data.employeeName },
    { label: "Employee Code", render: (data) => data.employeeId },
    {
      label: "Outstanding Principal",
      render: (data) => formatAmount(data.totalOutstandingPrincipal),
    },
    {
      label: "Outstanding Interest",
      render: (data) => formatAmount(data.totalOutstandingInterest),
    },
    {
      label: "Principal Paid",
      render: (data) => formatAmount(data.principalPaid),
    },
    {
      label: "Interest Paid",
      render: (data) => formatAmount(data.interestPaid),
    },
    {
      label: "Overdue Principal",
      render: (data) => formatAmount(data.overduePrincipal),
    },
    {
      label: "Overdue Interest",
      render: (data) => formatAmount(data.overdueInterest),
    },
    {
      label: "Total PAR",
      render: (data) => formatAmount(data.totalPAR),
    },
    {
      label: "DPD",
      render: (data) => data.dpd || 0,
    },
    {
      label: "Bucket",
      render: (data) => data.bucket || "0",
    },
    {
      label: "Last Paid Date",
      render: (data) => formatISODateToLocalDMY(data.lastPaidDate) || "-",
    },
    {
      label: "First Overdue Date",
      render: (data) => formatISODateToLocalDMY(data.firstOverdueDate) || "-",
    },
    {
      label: "Is Demised(Insurance)",
      render: (data) => "No",
    },
    {
      label: "EMI Start Date",
      render: (data) => {
        const { firstEmiDate } = getFirstAndLastEmiDates(
          data.branchManagerStatusUpdatedAt,
          data.proposedLoanDetails.tenureInMonths,
          data.emiDateByBranchManager,
          resolveEffectiveStartDate(data)
        );
        return firstEmiDate ? firstEmiDate.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" }) : "";
      },
    },
    { label: "Prospect ID", render: (data) => data.ApplicationId },
    {
      label: "Processing Fee",
      render: (data) => formatAmount(data.processingCharge || 0),
    },
    { label: "GST", render: (data) => formatAmount(data.gst || 0) },
    {
      label: "Insurance",
      render: (data) => formatAmount(data.insuranceAmount || 0),
    },
    {
      // Money written off when a foreclosure was settled below the payable
      // amount. Blank for loans that were never foreclosed.
      label: "Waiver Amount",
      render: (data) =>
        data?.fk_member_details_hasOne_member_foreclosure_approval_memberId
          ?.waiverAmount ?? "",
    },
    {
      label: "Waiver Reason",
      render: (data) =>
        data?.fk_member_details_hasOne_member_foreclosure_approval_memberId
          ?.waiverReason || "",
    },
    {
      label: "Security Deposit",
      render: (data) => formatAmount(data.securityDeposit || 0),
    },
    {
      label: "EMI Amount",
      render: (data) => formatAmount(data.emiAmount || 0),
    },
    {
      label: "Loan Purpose",
      render: (data) => data.businessDetails?.businessType || "-",
    },
    {
      label: "Aadhar Number",
      render: (data) => data.aadharNo,
    },
    {
      label: "Pan Number",
      render: (data) => (data.panNo ? data.panNo : ""),
    },
    {
      label: "Voter Id",
      render: (data) =>
        data.anotherIdentity === "Voter Id" ? data.anotherIdentityId : "",
    },
    {
      label: "Bank Account Number",
      render: (data) => data.bankDetails?.accountNumber || "-",
    },
    {
      label: "IFSC Code",
      render: (data) => data.bankDetails?.ifscCode || "-",
    },
    {
      label: "Bank Name",
      render: (data) => data.bankDetails?.bankName || "-",
    },
    {
      label: "Bank Branch",
      render: (data) => data.bankDetails?.bankBranch || "-",
    },
  ];

  return (
    <section className="master-layout">
      <div className="master-sidebar-wrapper">
        <Sidebar
          openSidebarToggle={openSidebarToggle}
          OpenSidebar={OpenSidebar}
        />
      </div>

      <div className="master-content-wrapper">
        <div className="master-header-wrapper">
          <DashboardHeader OpenSidebar={OpenSidebar} />
        </div>

        <div className="master-card-container">
          <div className="master-search-section">
            <h1 className="TableHeaderText w-100 text-start">GPL Master Data Report</h1>
            <div className="w-100">
              <Row className="w-100" style={{ fontWeight: "500" }}>
                <Col xs={12} lg={4} md={6} className="mb-2">
                  <Form.Group>
                    <Form.Label>Search Customer</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Search Name, Customer ID, Loan Acc No, Phone..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col xs={6} lg={2} md={6} className="mb-2">
                  <Form.Group>
                    <Form.Label>From Date</Form.Label>
                    <StandardizedDateInput
                      value={fromDate}
                      onChange={(event) => {
                        const val = event.target.value;
                        setFromDate(val);
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col xs={6} lg={2} md={6} className="mb-2">
                  <Form.Group>
                    <Form.Label>To Date</Form.Label>
                    <StandardizedDateInput
                      value={toDate}
                      onChange={(event) => {
                        const val = event.target.value;
                        setToDate(val);
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>

            <button
              className={`filter-toggle-btn ${showMobileFilters ? "active" : ""}`}
              onClick={() => setShowMobileFilters(!showMobileFilters)}
            >
              <span style={{ fontWeight: 600, fontSize: "14px" }}>
                {showMobileFilters ? "Hide Filters" : "Show Filters"}
              </span>
              <MdExpandMore size={22} />
            </button>

            <div className={`w-100 ${!showMobileFilters ? "mobile-hidden" : "mobile-visible"}`}>
              <Row className="w-100" style={{ fontWeight: "500" }}>

                <Col xs={12} lg={4} md={6} className="mb-2">
                  <Form.Label>Branch</Form.Label>
                  <Select
                    value={selectedBranchFilter}
                    onChange={(val) => {
                      setSelectedBranchFilter(val);
                      if (fromDate && toDate && val && selectedEmployeeFilter) {
                        setTimeout(() => setShowMobileFilters(false), 300);
                      }
                    }}
                    options={branchOptions}
                    isClearable
                    placeholder="Filter by Branch Name"
                    className="filter-dropdown"
                  />
                </Col>
                <Col xs={12} lg={4} md={6} className="mb-2">
                  <Form.Label>Employee</Form.Label>
                  <Select
                    value={selectedEmployeeFilter}
                    onChange={(val) => {
                      setSelectedEmployeeFilter(val);
                      if (fromDate && toDate && selectedBranchFilter && val) {
                        setTimeout(() => setShowMobileFilters(false), 300);
                      }
                    }}
                    options={employeeOptions}
                    isClearable
                    placeholder="Filter by Employee Name"
                    className="filter-dropdown"
                  />
                </Col>
              </Row>
            </div>
          </div>

          <div className="master-list-section">
            <div className="master-table-wrapper" style={{ padding: "0 15px" }}>
              <DataTable
                columns={columns}
                data={paginationData}
                reportName="GplMasterDataReport"
                fetchExcelData={fetchAllRowsForExport}
              />
            </div>

            <div className="master-pagination-wrapper">
              <CustomPagination
                totalPages={totalPages}
                currentPage={currentPage}
                paginate={paginate}
                totalItems={totalItems}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default GplMasterDataReport;
